// Phase-2 #33 — admin: browse past live sessions (with admin end-reasons)
// and delete old records. Backed by /liveSellingHistory/adminList + adminDelete.
import { useEffect, useState, useCallback } from "react";
import { apiInstanceFetch } from "../../../util/api";
import { setToast } from "../../../util/toast";
import dayjs from "dayjs";

const LIMIT = 20;

const LiveHistory = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [stuckOnly, setStuckOnly] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(() => {
    setLoading(true);
    apiInstanceFetch
      .get(`liveSellingHistory/adminList?page=${page}&limit=${LIMIT}${stuckOnly ? "&stuck=1" : ""}`)
      .then((res) => {
        if (res?.status) {
          setRows(res.data || []);
          setTotal(res.total || 0);
        }
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, [page, stuckOnly]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = (row) => {
    if (row.isCurrentlyLive) return;
    if (!window.confirm("Delete this live session record? This cannot be undone.")) return;
    apiInstanceFetch
      .post("liveSellingHistory/adminDelete", { id: row._id })
      .then((res) => {
        if (res?.status) {
          setToast("success", res.message || "Deleted.");
          setRows((prev) => prev.filter((r) => r._id !== row._id));
          setTotal((t) => Math.max(0, t - 1));
        } else {
          setToast("error", res?.message || "Delete failed.");
        }
      })
      .catch(() => setToast("error", "Delete failed."));
  };

  const hostName = (r) => {
    const s = r.sellerId;
    if (s && (s.businessName || s.firstName)) {
      return s.businessName || `${s.firstName || ""} ${s.lastName || ""}`.trim();
    }
    // Creator lives store the host's User id in sellerId, so the Seller
    // populate comes back empty — still a valid, deletable record.
    return "Creator live";
  };

  const fmt = (t) => (t ? dayjs(t).format("DD MMM YYYY, HH:mm") : "—");
  const pages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <div className="userMain">
      <div className="tableMain mt-3">
        <div className="d-flex justify-content-between align-items-center px-3 pt-2 pb-2 flex-wrap">
          <div>
            <h4>Live History</h4>
            <small className="text-secondary">
              Past live sessions — end reasons are recorded when an admin stops a stream.
            </small>
          </div>
          <label className="d-flex align-items-center gap-2 mb-0" style={{ cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={stuckOnly}
              onChange={(e) => {
                setPage(1);
                setStuckOnly(e.target.checked);
              }}
            />
            <span>Stuck sessions only (never ended)</span>
          </label>
        </div>

        <div className="table-responsive px-3 pb-3">
          <table className="table align-middle">
            <thead>
              <tr>
                <th>No</th>
                <th>Host</th>
                <th>Started</th>
                <th>Ended</th>
                <th>Duration</th>
                <th>Viewers</th>
                <th>End reason</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9}>Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={9}>No live history found.</td></tr>
              ) : (
                rows.map((r, idx) => (
                  <tr key={r._id}>
                    <td>{(page - 1) * LIMIT + idx + 1}</td>
                    <td>{hostName(r)}</td>
                    <td><small>{fmt(r.startTime)}</small></td>
                    <td><small>{fmt(r.endTime)}</small></td>
                    <td>{r.duration || "—"}</td>
                    <td>
                      {r.totalUser || 0}
                      {r.peakViewers ? (
                        <small className="text-secondary"> (peak {r.peakViewers})</small>
                      ) : null}
                    </td>
                    <td>
                      {r.endedByAdmin ? (
                        <span>
                          <span className="badge" style={{ background: "#dc3545" }}>admin</span>{" "}
                          <small>{r.endReason || "—"}</small>
                        </span>
                      ) : (
                        <small className="text-secondary">—</small>
                      )}
                    </td>
                    <td>
                      {r.isCurrentlyLive ? (
                        <span className="badge" style={{ background: "#28a745" }}>LIVE</span>
                      ) : r.endTime ? (
                        <span className="badge" style={{ background: "#6c757d" }}>Ended</span>
                      ) : (
                        <span className="badge" style={{ background: "#F0C420", color: "#1A1614" }}>Stuck</span>
                      )}
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        disabled={r.isCurrentlyLive}
                        title={r.isCurrentlyLive ? "End the stream first" : "Delete this record"}
                        onClick={() => handleDelete(r)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="d-flex justify-content-between align-items-center px-3 pb-3">
          <small className="text-secondary">{total} session(s)</small>
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-outline-light"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              ‹ Prev
            </button>
            <span className="align-self-center">
              Page {page} / {pages}
            </span>
            <button
              className="btn btn-sm btn-outline-light"
              disabled={page >= pages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next ›
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveHistory;
